const CHECKBOX_COLUMN_WIDTH_PX = 48

export interface GenericTableColumnWidthConfig {
  width?: string
  widthXl?: string
  minWidth?: string
  maxWidth?: string
  columnSize?: string
}

/**
 * Parses a CSS pixel size string.
 * @param value {string | undefined} - The CSS size value.
 * @returns {number | null} - The pixel value, or null when not a plain px size.
 */
export function parseCssPxSize(value: string | undefined): number | null {
  if (!value) return null

  const match = value.trim().match(/^(\d+(?:\.\d+)?)px$/)
  return match ? Number.parseFloat(match[1]) : null
}

/**
 * Resolves the design-time column width for the active viewport breakpoint.
 * @param column {GenericTableColumnWidthConfig} - The column width configuration.
 * @param isXlViewport {boolean} - Whether the XL breakpoint is active.
 * @returns {number | null} - The base width in pixels.
 */
export function resolveColumnBaseWidthPx(column: GenericTableColumnWidthConfig, isXlViewport: boolean): number | null {
  const width = isXlViewport ? column.widthXl ?? column.width : column.width
  return parseCssPxSize(width)
}

/**
 * Builds a flexible grid track when a column does not use a fixed pixel width.
 * @param column {GenericTableColumnWidthConfig} - The column width configuration.
 * @returns {string} - The CSS grid track size.
 */
export function buildFlexibleColumnTrack(column: GenericTableColumnWidthConfig): string {
  if (column.columnSize) return column.columnSize

  const minWidth = column.minWidth
  const maxWidth = column.maxWidth

  if (minWidth && maxWidth) return `minmax(${minWidth}, ${maxWidth})`
  if (minWidth) return `minmax(${minWidth}, 1fr)`
  if (maxWidth) return `minmax(0, ${maxWidth})`

  return '1fr'
}

/**
 * Builds grid template columns that scale fixed pixel widths proportionally to the container.
 * Falls back to static tracks when any column uses a non-pixel width strategy.
 * @param columns {GenericTableColumnWidthConfig[]} - The table column width configurations.
 * @param containerWidth {number} - The table container width in pixels.
 * @param selectable {boolean} - Whether the table has a selection column.
 * @param isXlViewport {boolean} - Whether the XL breakpoint is active.
 * @returns {string | null} - The grid template columns value, or null to use static sizing.
 */
export function buildResponsiveGridTemplateColumns(columns: GenericTableColumnWidthConfig[], containerWidth: number, selectable: boolean, isXlViewport: boolean): string | null {
  if (!containerWidth || !columns.length) return null

  const baseWidths = columns.map((column) => resolveColumnBaseWidthPx(column, isXlViewport))
  const hasFlexibleColumn = baseWidths.some((width) => width == null)

  if (hasFlexibleColumn) return null

  const availableWidth = Math.max(containerWidth - (selectable ? CHECKBOX_COLUMN_WIDTH_PX : 0), 0)
  const designTotal = baseWidths.reduce<number>((sum, width) => sum + width!, 0)

  if (!designTotal || !availableWidth) return null

  const scale = availableWidth / designTotal
  const tracks = baseWidths.map((width, index) => {
    const scaled = Math.max(1, Math.round(width! * scale))
    const maxWidthPx = parseCssPxSize(columns[index].maxWidth)

    if (maxWidthPx != null) {
      return `minmax(0, ${Math.min(maxWidthPx, scaled)}px)`
    }

    return `${scaled}px`
  })

  return selectable ? [CHECKBOX_COLUMN_WIDTH_PX + 'px', ...tracks].join(' ') : tracks.join(' ')
}

/**
 * Builds static grid template columns from column definitions.
 * @param columns {GenericTableColumnWidthConfig[]} - The table column width configurations.
 * @param selectable {boolean} - Whether the table has a selection column.
 * @param isXlViewport {boolean} - Whether the XL breakpoint is active.
 * @returns {string} - The grid template columns value.
 */
export function buildStaticGridTemplateColumns(columns: GenericTableColumnWidthConfig[], selectable: boolean, isXlViewport: boolean): string {
  const tracks = columns.map((column) => resolveStaticColumnTrack(column, isXlViewport))

  return selectable ? [CHECKBOX_COLUMN_WIDTH_PX + 'px', ...tracks].join(' ') : tracks.join(' ')
}

/**
 * Resolves a static grid track for a single column.
 * @param column {GenericTableColumnWidthConfig} - The column width configuration.
 * @param isXlViewport {boolean} - Whether the XL breakpoint is active.
 * @returns {string} - The CSS grid track size.
 */
export function resolveStaticColumnTrack(column: GenericTableColumnWidthConfig, isXlViewport: boolean): string {
  const width = isXlViewport ? column.widthXl ?? column.width : column.width
  if (width) return width

  return buildFlexibleColumnTrack(column)
}
